import { createLogger } from './logger';
import { isTokenExpired } from './jwt';

const log = createLogger('api');

/**
 * Base URLs for the two apps.
 *
 * - `LIGHTNING_API_BASE` falls back to the legacy `VITE_API_BASE` env var
 *   so existing builds and any external scripts keep working unchanged.
 * - `VIDEO_API_BASE` is set to `/api/video` for `make deploy` builds and
 *   to `${LIGHTNING_API_BASE}/api/video` for CI deploys. Both apps now
 *   ship as a single backend service — the video FastAPI app is mounted
 *   in-process at `/api/video/*` inside `lightning.server` — so the SPA
 *   only needs the lightning host plus a path prefix to reach the video
 *   routes. It can only be empty when a developer runs `npm run dev` /
 *   `make web-dev` without setting it in their `.env`; in that case
 *   `isVideoEnabled()` returns false and the Video tab is hidden so
 *   the SPA doesn't surface a dead link.
 */
export const LIGHTNING_API_BASE =
  import.meta.env.VITE_LIGHTNING_API_BASE ||
  import.meta.env.VITE_API_BASE ||
  '';
export const VIDEO_API_BASE = import.meta.env.VITE_VIDEO_API_BASE || '';

/** Back-compat alias for callers written before the split. */
export const API_BASE = LIGHTNING_API_BASE;

export type ApiApp = 'lightning' | 'video';

/**
 * True in every official deployment (CI + `make deploy` always set
 * `VITE_VIDEO_API_BASE`). The check only gates the Video tab in
 * dev-mode setups where the env var wasn't configured.
 */
export function isVideoEnabled(): boolean {
  return Boolean(VIDEO_API_BASE);
}

const TOKEN_STORAGE_KEY = 'idp_token';

/**
 * Window event dispatched whenever we detect that the current session is no
 * longer valid (token expired locally, or the API rejected us with 401/403).
 *
 * `AuthProvider` listens for this and triggers a logout, which sends the user
 * back to the login screen instead of leaving the UI in a stuck "connected"
 * state with stale data — this is what used to require a manual logout +
 * Ctrl-Shift-R after the tab sat overnight.
 */
export const AUTH_EXPIRED_EVENT = 'auth:expired';

export interface AuthExpiredDetail {
  reason: string;
}

function dispatchAuthExpired(reason: string): void {
  log.info(`Session invalid (${reason}); requesting logout`);
  window.dispatchEvent(
    new CustomEvent<AuthExpiredDetail>(AUTH_EXPIRED_EVENT, {
      detail: { reason },
    })
  );
}

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

export class SessionExpiredError extends Error {
  constructor(reason: string) {
    super(`Session expired (${reason}). Please sign in again.`);
    this.name = 'SessionExpiredError';
  }
}

export interface ApiFetchInit extends RequestInit {
  /**
   * Which backend to target. Defaults to `'lightning'` so the existing
   * lightning callers keep working unchanged. Pass `'video'` for the
   * 360-video service.
   */
  app?: ApiApp;
}

/**
 * Centralized fetch wrapper for all backend API calls.
 *
 *  - Prepends the appropriate base URL (`LIGHTNING_API_BASE` or
 *    `VIDEO_API_BASE`) for relative paths. Callers can also pass full URLs.
 *  - Attaches `Authorization: Bearer <idp_token>` when present.
 *  - Refuses to send a request whose token is already expired and signals the
 *    auth provider to log the user out instead.
 *  - On 401/403 from the backend, fires the same logout signal so the UI
 *    immediately reflects the invalid session.
 */
export async function apiFetch(
  input: string,
  init: ApiFetchInit = {}
): Promise<Response> {
  const token = getStoredToken();

  if (token && isTokenExpired(token)) {
    dispatchAuthExpired('token-expired-preflight');
    throw new SessionExpiredError('token-expired-preflight');
  }

  const headers = new Headers(init.headers);
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const base = init.app === 'video' ? VIDEO_API_BASE : LIGHTNING_API_BASE;
  const url = /^https?:\/\//i.test(input) ? input : `${base}${input}`;
  const { app: _app, ...fetchInit } = init;
  const response = await fetch(url, { ...fetchInit, headers });

  if (response.status === 401 || response.status === 403) {
    dispatchAuthExpired(`http-${response.status}`);
  }

  return response;
}
